import { useMemo, useState } from "react";
import {
  Banner,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  Field,
  PageHead,
  Panel,
  PersonCell,
  Pill,
  StatusPill,
  Tabs,
  Textarea,
  type Column,
} from "@guestpilot/ui-kit";
import { useGet, useWrite } from "../api/hooks";
import type { Schema } from "../api/types";
import { useHandleApiError } from "../errors";
import { formatDate, formatDateTime } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";

type Removal = Schema<"RemovalRequest">;
type Action = "validate" | "complete" | "reject" | "execute_now";

const TABS = {
  pending: ["pending", "scheduled", "executing", "failed"],
  completed: ["completed"],
  rejected: ["rejected", "cancelled"],
} as const;
type Tab = keyof typeof TABS;

/**
 * Removal requests end to end (BR-090…BR-094, BR-164, BR-165, BR-210, BR-213): an Admin validates (the mode decides what happens next),
 * marks a manual removal as completed, or rejects with a reason.
 */
export function Removals() {
  const { t, locale } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const list = useGet<Removal[]>("/api/v1/removals");
  const [tab, setTab] = useState<Tab>("pending");
  const [action, setAction] = useState<{ r: Removal; kind: Action } | null>(null);
  const tz = s.me.tenant.timeZone;
  const rows = list.data;
  const counts = useMemo(
    () =>
      Object.fromEntries(
        (Object.keys(TABS) as Tab[]).map((k) => [
          k,
          (rows ?? []).filter((r) => (TABS[k] as readonly string[]).includes(r.status ?? "")).length,
        ]),
      ) as Record<Tab, number>,
    [rows],
  );
  const shown = (rows ?? []).filter((r) => (TABS[tab] as readonly string[]).includes(r.status ?? ""));

  const columns: Column<Removal>[] = [
    {
      key: "person",
      header: t("ui:users.col.person"),
      render: (r) => (
        <PersonCell
          name={r.participant?.displayName || r.participant?.email || "—"}
          sub={r.participant?.displayName ? r.participant.email : undefined}
        />
      ),
    },
    { key: "team", header: t("ui:users.col.team"), render: (r) => <Pill>{r.team?.displayName ?? "—"}</Pill> },
    {
      key: "by",
      header: t("ui:removals.col.by"),
      render: (r) =>
        r.requestedBy ? r.requestedBy.displayName : t(`ui:removals.byType.${r.requestedByType ?? "system"}`),
    },
    { key: "at", header: t("ui:removals.col.at"), render: (r) => formatDate(r.requestedAt, locale, tz) },
    {
      key: "reason",
      header: t("ui:removals.col.reason"),
      render: (r) => <span title={r.reason}>{r.reason}</span>,
    },
    {
      key: "status",
      header: t("ui:users.col.status"),
      render: (r) => (
        <div className="gp-person-text">
          <StatusPill
            domain="removal"
            value={r.status ?? "pending"}
            label={t(`ui:removalStatus.${r.status}`)}
          />
          {r.status === "scheduled" && r.executeAt ? (
            <span className="t-body-sm gp-faint">
              {t("ui:removals.executeAt", { date: formatDateTime(r.executeAt, locale, tz) })}
            </span>
          ) : null}
          {r.status === "failed" && r.failureCode ? (
            <span className="t-body-sm gp-faint">
              {t(`errors:${r.failureCode}`) === `errors:${r.failureCode}`
                ? r.failureCode
                : t(`errors:${r.failureCode}`)}
            </span>
          ) : null}
          {r.probablyDone ? (
            <span className="t-body-sm gp-faint">{t("ui:removals.probablyDone")}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "actions",
      header: t("ui:table.actions"),
      render: (r) => {
        const open = r.status === "pending";
        const validate = s.gate("REMOVAL_VALIDATE");
        return (
          <div className="gp-row">
            {open && !r.manualOnly ? (
              <Button
                variant="primary"
                disabledReason={validate}
                onClick={() => setAction({ r, kind: "validate" })}
              >
                {t("ui:removals.validate")}
              </Button>
            ) : null}
            {r.status === "scheduled" ? (
              <Button
                variant="danger"
                disabledReason={validate}
                onClick={() => setAction({ r, kind: "execute_now" })}
              >
                {t("ui:removals.executeNow")}
              </Button>
            ) : null}
            {open || r.status === "failed" ? (
              <Button
                variant="ghost"
                disabledReason={validate}
                onClick={() => setAction({ r, kind: "complete" })}
              >
                {t("ui:removals.complete")}
              </Button>
            ) : null}
            {open ? (
              <Button
                variant="ghost"
                disabledReason={validate}
                onClick={() => setAction({ r, kind: "reject" })}
              >
                {t("ui:removals.reject")}
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="gp-page">
      <PageHead title={t("ui:removals.title")} subtitle={t("ui:removals.lead")} />
      <Tabs
        label={t("ui:removals.title")}
        value={tab}
        onChange={(k) => setTab(k as Tab)}
        tabs={(Object.keys(TABS) as Tab[]).map((k) => ({
          key: k,
          label: t(`ui:removals.tab.${k}`),
          count: counts[k],
        }))}
      />
      <Panel label={t("ui:removals.title")}>
        <DataTable
          caption={t("ui:removals.title")}
          columns={columns}
          rows={rows ? shown : undefined}
          loading={list.isLoading}
          error={list.isError}
          onRetry={() => void list.refetch()}
          errorTitle={t("ui:table.error")}
          retryLabel={t("common:retry")}
          rowKey={(r) => r.id ?? ""}
          emptyTitle={t("ui:removals.empty")}
        />
      </Panel>
      <ActionDialog item={action} onClose={() => setAction(null)} fail={fail} />
    </div>
  );
}

function ActionDialog({
  item,
  onClose,
  fail,
}: {
  item: { r: Removal; kind: Action } | null;
  onClose: () => void;
  fail: (e: unknown) => void;
}) {
  const { t } = useI18n();
  const invalidate = ["/api/v1/removals", "/api/v1/participants", "/api/v1/teams", "/api/v1/dashboard"];
  const validate = useWrite<Removal, Schema<"RemovalAction">>(
    "POST",
    "/api/v1/removals/:id/validate",
    invalidate,
  );
  const complete = useWrite<Removal, Schema<"RemovalAction">>(
    "POST",
    "/api/v1/removals/:id/complete",
    invalidate,
  );
  const executeNow = useWrite<Removal, Schema<"RemovalAction">>(
    "POST",
    "/api/v1/removals/:id/execute-now",
    invalidate,
  );
  const reject = useWrite<Removal, Schema<"RemovalAction">>(
    "POST",
    "/api/v1/removals/:id/reject",
    invalidate,
  );
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  if (!item) return null;
  const { r, kind } = item;
  const irreversible =
    (kind === "validate" || kind === "execute_now") &&
    r.modeAtRequest !== undefined &&
    r.modeAtRequest !== "off";
  const mutation =
    kind === "validate"
      ? validate
      : kind === "complete"
        ? complete
        : kind === "execute_now"
          ? executeNow
          : reject;
  const needsReason = kind === "reject";
  const name = r.participant?.displayName || r.participant?.email || "";
  return (
    <Dialog
      open
      onClose={onClose}
      title={t(`ui:removals.dialog.${kind}.title`, { name })}
      description={t(
        `ui:removals.dialog.${kind}.body${kind === "validate" ? `.${r.modeAtRequest ?? "off"}` : ""}`,
      )}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant={irreversible || kind === "reject" ? "danger" : "primary"}
            disabledReason={
              needsReason && !reason.trim()
                ? t("ui:removal.reasonRequired")
                : irreversible && !confirmed
                  ? t("ui:removal.confirmRequired")
                  : undefined
            }
            onClick={() =>
              mutation.mutate(
                {
                  params: { id: r.id ?? "" },
                  body: {
                    version: r.version ?? 0,
                    ...(reason ? { reason } : {}),
                    ...(irreversible ? { confirmIrreversible: true } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    setReason("");
                    setConfirmed(false);
                    onClose();
                  },
                  onError: fail,
                },
              )
            }
          >
            {t(`ui:removals.dialog.${kind}.action`)}
          </Button>
        </>
      }
    >
      {r.modeAtRequest === "entra_account" && (kind === "validate" || kind === "execute_now") ? (
        <Banner tone="warn">{t("ui:removal.outsideScope")}</Banner>
      ) : null}
      {(r.otherControlledTeams ?? []).length > 0 && (kind === "validate" || kind === "execute_now") ? (
        <Banner tone="warn">
          {t("ui:removal.otherTeams", {
            teams: (r.otherControlledTeams ?? []).map((x) => x.displayName).join(", "),
          })}
        </Banner>
      ) : null}
      {needsReason ? (
        <Field label={t("ui:removals.rejectReason")}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      ) : null}
      {irreversible ? (
        <Checkbox checked={confirmed} onChange={setConfirmed} label={t("ui:removal.irreversible")} />
      ) : null}
    </Dialog>
  );
}
